import * as R from 'ramda';

export type PassType = 'ignite' | 'vaal' | 'blueNile' | 'uniqueness' | 'ageCheck13' | 'faceAgeCheck13';

export enum IdExportType {
  POLYGON = 'POLYGON',
}
export type IdExportDetail = {
  name: string;
  type: IdExportType;
  logo?: string;
};

type PortalConfigs = {
  gatewayStage: string;
  network: string;
  passConfigs: Record<string, PassConfig>;
};

export type ChainNetworkConfig = {
  useClientSends: boolean;
};

export type PassConfig = {
  gatekeeperNetworkPublicKey: string; // We have to define the networks supported by the portal, so they're defined here
  useClientSends: boolean;
  chainConfig?: Record<number, ChainNetworkConfig>;
};

const supportedTestDomains = ['demo', 'localhost', 'dev', 'preprod'];

// create a function that returns if the domain is a test domain
export const isDemoDomain = (domain: string = window.location.hostname): boolean =>
  supportedTestDomains.some((testDomain) => domain.includes(testDomain));

const devPassConfig = {
  ignite: {
    gatekeeperNetworkPublicKey: 'tigoYhp9SpCDoCQmXGj2im5xa3mnjR1zuXrpCJ5ZRmi',
    useClientSends: true,
    chainConfig: {
      // Ethereum homestead
      1: {
        useClientSends: true,
      },
      solana: {
        useClientSends: true,
      },
    },
  },
  uniqueness: {
    gatekeeperNetworkPublicKey: 'tunQheuPpHhjjsbrUDp4rikqYez9UXv4SXLRHf9Kzsv',
    useClientSends: true,
    chainConfig: {
      // Ethereum homestead
      1: {
        useClientSends: true,
      },
      solana: {
        useClientSends: true,
      },
    },
  },
  ageCheck13: {
    gatekeeperNetworkPublicKey: 'ta136ftcpd8RyTYu3FGBjstD7EMXGBWrcwotdScrAKu',
    useClientSends: true,
    chainConfig: {
      // Ethereum homestead
      1: {
        useClientSends: true,
      },
      solana: {
        useClientSends: true,
      },
    },
  },
};

const preprodPassConfig = {
  ignite: {
    gatekeeperNetworkPublicKey: 'tigoYhp9SpCDoCQmXGj2im5xa3mnjR1zuXrpCJ5ZRmi',
    useClientSends: true,
    chainConfig: {
      // Ethereum homestead
      1: {
        useClientSends: true,
      },
      solana: {
        useClientSends: true,
      },
    },
  },
  uniqueness: {
    gatekeeperNetworkPublicKey: 'tunQheuPpHhjjsbrUDp4rikqYez9UXv4SXLRHf9Kzsv',
    useClientSends: true,
    chainConfig: {
      // Ethereum homestead
      1: {
        useClientSends: true,
      },
      solana: {
        useClientSends: true,
      },
    },
  },
  ageCheck13: {
    gatekeeperNetworkPublicKey: 'ta136ftcpd8RyTYu3FGBjstD7EMXGBWrcwotdScrAKu',
    useClientSends: true,
    chainConfig: {
      // Ethereum homestead
      1: {
        useClientSends: true,
      },
      solana: {
        useClientSends: true,
      },
    },
  },
};

const prodPassConfig = {
  ignite: {
    gatekeeperNetworkPublicKey: 'ignREusXmGrscGNUesoU9mxfds9AiYTezUKex2PsZV6',
    useClientSends: true,
    chainConfig: {
      // Ethereum homestead
      1: {
        useClientSends: true,
      },
      solana: {
        useClientSends: true,
      },
    },
  },
  uniqueness: {
    gatekeeperNetworkPublicKey: 'uniqobk8oGh4XBLMqM68K8M2zNu3CdYX7q5go7whQiv',
    useClientSends: true,
    chainConfig: {
      // Ethereum homestead
      1: {
        useClientSends: true,
      },
      solana: {
        useClientSends: true,
      },
    },
  },
  ageCheck13: {
    gatekeeperNetworkPublicKey: 'ag13vc1KaV5kMjp3Gb5yTq8cyZSnHPVkTHdNJ2fWXk1',
    useClientSends: true,
    chainConfig: {
      // Ethereum homestead
      1: {
        useClientSends: true,
      },
      solana: {
        useClientSends: true,
      },
    },
  },
};

export const defaultDevNetwork = process.env.DEFAULT_CHAIN_NETWORK || 'sepolia';
export const defaultProdNetwork = process.env.DEFAULT_CHAIN_NETWORK || 'sepolia';
export const DEFAULT_CHAIN_NETWORK =
  process.env.REACT_APP_STAGE === 'production' ? defaultProdNetwork : defaultDevNetwork;

const portalConfigs: Record<string, PortalConfigs> = {
  e2e: {
    gatewayStage: 'dev',
    network: defaultDevNetwork,
    passConfigs: devPassConfig,
  },
  deployment: {
    gatewayStage: 'dev',
    network: defaultDevNetwork,
    passConfigs: devPassConfig,
  },
  preview: {
    gatewayStage: 'preprod',
    network: defaultDevNetwork,
    passConfigs: preprodPassConfig,
  },
  production: {
    gatewayStage: 'prod',
    network: defaultProdNetwork,
    passConfigs: prodPassConfig,
  },
};

export type PortalConfig = PortalConfigs &
  PassConfig & {
    i18nContext: PassType;
  };

export const defaultPassType = (process.env.REACT_APP_PASS_TYPE as PassType) || 'uniqueness';

export type ChainInfo = {
  id: number | string;
  name: string;
};

function getPortalConfig(passType = defaultPassType, chain: ChainInfo | undefined = undefined): PortalConfig {
  const stage = process.env.REACT_APP_STAGE || 'production';
  const config = R.propOr<PortalConfigs, Record<string, PortalConfigs>, PortalConfigs>(
    portalConfigs.dev,
    stage,
    portalConfigs
  );
  const passConfig = config.passConfigs[passType];
  const chainConfig = passConfig.chainConfig && chain ? passConfig.chainConfig[chain.id] : undefined;
  const configClientSends = chainConfig !== undefined ? chainConfig.useClientSends : passConfig.useClientSends;

  // Check if the env var is set, if not, check the chain network config,
  // if not, default to the top level config

  const useClientSends = `${process.env.REACT_APP_CLIENT_SENDS_ENABLED}`.toLowerCase() === 'true' || configClientSends;

  return {
    ...config,
    network: chain?.name ?? 'unknown',
    gatekeeperNetworkPublicKey: config.passConfigs[passType].gatekeeperNetworkPublicKey,
    useClientSends,
    i18nContext: passType,
  };
}

export { getPortalConfig };
